import React, { useState } from 'react';
import { Space } from 'antd';
import { Notice } from '../components/common/Notice';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { LoggedOutHeroMessage } from '../components/common/LoggedOutHeroMessage';
import { LoggedInHeroMessage } from '../components/common/LoggedInHeroMessage';
import { ProfileStatusCard } from '../components/participant/ProfileStatusCard';
import { EngagementCycleSummaryCard } from '../components/engagement/EngagementCycleSummaryCard';
import { PublicHomeLayout } from '../components/layout/PublicHomeLayout';
import { PublicSurveyTabs } from '../components/survey/PublicSurveyTabs';
import { ParticipantSurveyTabs } from '../components/survey/ParticipantSurveyTabs';
import { usePublicSurveyLists } from '../hooks/usePublicSurveyLists';
import { usePublicSurveyParticipation } from '../hooks/usePublicSurveyParticipation';
import { useCycleHistory } from '../hooks/useEngagementTickets';
import { participantService } from '../services/participant.service';
import { engagementService } from '../services/engagement.service';
import { ProfileStatus } from '../types/participant.types';

export const PublicHomePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { startParticipation } = usePublicSurveyParticipation();
  const isParticipant = isAuthenticated && user?.role === 'PARTICIPANT';

  const {
    activePage,
    setActivePage,
    completedPage,
    setCompletedPage,
    activeQuery,
    completedQuery,
  } = usePublicSurveyLists({ enabled: !isParticipant });

  const [answeredPage, setAnsweredPage] = useState(1);
  const answeredPageSize = 10;

  const { data: engagementData } = useQuery({
    queryKey: ['participant-referral-link', user?.userId],
    queryFn: () => engagementService.getCurrentCycle({ page: 1, pageSize: 10 }),
    enabled: isParticipant,
    staleTime: 5 * 60 * 1000,
  });

  const { data: dashboardData, isLoading: dashboardLoading, error: dashboardError } = useQuery({
    queryKey: ['dashboard-tabs', user?.userId, answeredPage, answeredPageSize],
    queryFn: () => participantService.getDashboard(answeredPage, answeredPageSize),
    enabled: isParticipant,
    staleTime: 5 * 60 * 1000,
  });

  const { data: referralsData } = useQuery({
    queryKey: ['participant-referrals-chip', user?.userId],
    queryFn: () => engagementService.listReferrals({ page: 1, pageSize: 50 }),
    enabled: isParticipant,
    staleTime: 5 * 60 * 1000,
  });

  const { data: publicCycleData } = useQuery({
    queryKey: ['public-engagement-current-cycle'],
    queryFn: () => engagementService.getPublicCurrentCycle(),
    enabled: !isParticipant,
    staleTime: 5 * 60 * 1000,
  });

  const { data: cycleHistoryData } = useCycleHistory(1, 10, { enabled: isParticipant });
  const lastResultsCycleKey = cycleHistoryData?.cycles.find((c) => c.hasResults)?.cycleKey ?? null;

  const ninetyDaysAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
  const qualifiedReferrals90d = referralsData?.items.filter(
    (r) => r.status === 'QUALIFIED' && r.qualifiedAt && new Date(r.qualifiedAt) >= ninetyDaysAgo,
  ).length;

  const publicMessage = (location.state as { publicMessage?: string } | null)?.publicMessage;

  const profileNeedsAction = !!dashboardData?.profile && (
    dashboardData.profile.status !== ProfileStatus.CURRENT || !!dashboardData.profile.isIncomplete
  );

  const buildSurveyShareUrl = (surveyId: string, publicAuditHash?: string | null) => {
    const referralCode = engagementData?.referralLink?.code;

    if (publicAuditHash) {
      const base = new URL(`/public/surveys/${publicAuditHash}`, window.location.origin);
      if (referralCode) base.searchParams.set('ref', referralCode);
      return base.toString();
    }

    const base = new URL('/signup', window.location.origin);
    base.searchParams.set('surveyId', surveyId);
    if (referralCode) base.searchParams.set('ref', referralCode);
    return base.toString();
  };

  return (
    <PublicHomeLayout>
      {isAuthenticated ? (
        <LoggedInHeroMessage
          answeredCount={dashboardData?.activeSummary?.answeredCount}
          totalActiveCount={dashboardData?.activeSummary?.total}
          cyclePoints={dashboardData?.currentCyclePoints ?? undefined}
          qualifiedReferrals90d={qualifiedReferrals90d}
          isLoading={dashboardLoading}
        />
      ) : (
        <LoggedOutHeroMessage />
      )}

      <div id="survey-lists" style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 24px 64px' }}>
        <Space direction="vertical" size={24} style={{ width: '100%' }}>
          {publicMessage && <Notice type="warning" message={publicMessage} showIcon />}

          {isParticipant && profileNeedsAction && (
            <ProfileStatusCard
              profile={dashboardData?.profile || null}
              isLoading={dashboardLoading}
              error={dashboardError ? (dashboardError as Error).message : null}
              onEditClick={() => navigate('/profile', { state: { isEditing: true } })}
            />
          )}

          {isParticipant ? (
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
              <EngagementCycleSummaryCard
                cycle={engagementData?.cycle}
                onCtaClick={() => navigate('/engagement')}
                participantProgress={
                  engagementData?.participant
                    ? {
                        totalPoints: engagementData.participant.totalPoints,
                        eligibleForPrize: engagementData.participant.eligibleForPrize,
                        pointsToPrizeThreshold: engagementData.rankingSnapshot?.pointsToPrizeThreshold ?? 0,
                      }
                    : null
                }
                referralUrl={engagementData?.referralLink?.url}
                lastResultsCycleKey={lastResultsCycleKey}
              />
              <ParticipantSurveyTabs
                userId={user?.userId}
                activePage={activePage}
                onActivePage={setActivePage}
                onParticipate={startParticipation}
                getShareUrl={buildSurveyShareUrl}
                answeredSurveys={dashboardData?.completedSurveys?.surveys || []}
                answeredTotal={dashboardData?.completedSurveys?.total || 0}
                answeredPage={dashboardData?.completedSurveys?.page || answeredPage}
                answeredPageSize={dashboardData?.completedSurveys?.pageSize || answeredPageSize}
                answeredTotalPages={dashboardData?.completedSurveys?.totalPages || 0}
                answeredLoading={dashboardLoading}
                answeredError={dashboardError ? (dashboardError as Error).message : null}
                onAnsweredPageChange={(page) => setAnsweredPage(page)}
              />
            </Space>
          ) : (
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
              <EngagementCycleSummaryCard cycle={publicCycleData} />
              <PublicSurveyTabs
                activeData={activeQuery.data}
                completedData={completedQuery.data}
                activeLoading={activeQuery.isLoading}
                completedLoading={completedQuery.isLoading}
                activePage={activePage}
                completedPage={completedPage}
                onActivePage={setActivePage}
                onCompletedPage={setCompletedPage}
                onParticipate={startParticipation}
              />
            </Space>
          )}
        </Space>
      </div>
    </PublicHomeLayout>
  );
};
